import axios, { CancelTokenSource } from 'axios';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import UpsertCompanyForm from 'components/guarantors/company/UpsertCompanyForm';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import createDecorator from 'final-form-calculate';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Icon, Modal } from 'semantic-ui-react';
import {
  deleteCompany,
  findCompanyById,
  getAllGuarantorCompanies,
  searchCompanies,
  getAllGuarantors
} from 'services/companyServices';
import { debounce } from 'ts-debounce';
import { RepetitiveTestingMode } from 'ts-types/api.enums';
import { CompanyDto, CompanySearchRequest, UpsertCompanyDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { getAllMandatesWithDeleted } from 'services/mandateServices';
import styled from 'styled-components';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';

const LeftSearchContainer = styled.div`
  width: 50%
`;

const RightSearchContainer = styled.div`
  width: 50%
`;

interface CompanySearch extends CompanySearchRequest {
  guarantorCompanies: Array<DropdownOption>;
}
const initialCompaniesSearch: Partial<CompanySearch> = {
  includesInactive: false,
  inactiveOnly: false,
  selfOnboarding: false,
  guarantorCompany: false
};

interface CompanyFieldProps extends FormState {
  values: Partial<CompanySearch>
}

interface Props extends
    MessageSourceProps,
    AuthConsumerRenderProps,
    GuarantorsConsumerRenderProps {
}

interface State {
  referenceDataLoaded: boolean,
  foundCompanies: Array<CompanyDto>,
  guarantorCompanies: Array<CompanyDto>,
  allGuarantorCompanies: Array<CompanyDto>,
  repetitiveTestingModeOptions: Array<DropdownOption>,
  superMandateSearchOptions: Array<DropdownOption>,
  companiesLoaded: boolean,
  addEditModalOpen: boolean,
  company?: UpsertCompanyDto,
  companyId?: number,
  searchValues: Partial<CompanySearch>,
  cancelTokenSource: CancelTokenSource,
}

class CompaniesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {getMessage, currentUser} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const repetitiveTestingModes = stringArrayToDropdownOptionArray(
        Object.values(RepetitiveTestingMode), getMessage, "repetitiveTestingMode");

    const mandateId = currentUser!.userConfig.mandateId;

    initialCompaniesSearch.mandateId = mandateId;

    this.state = {
      referenceDataLoaded: false,
      foundCompanies: [],
      guarantorCompanies: [],
      allGuarantorCompanies: [],
      companiesLoaded: false,
      addEditModalOpen: false,
      company: undefined,
      companyId: undefined,
      searchValues: initialCompaniesSearch,
      repetitiveTestingModeOptions: repetitiveTestingModes,
      superMandateSearchOptions: [],
      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {

    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    try {
      const guarantorCompanies = await getAllGuarantorCompanies(mandateId, cancelTokenSource);
      const allGuarantorCompanies = await getAllGuarantors(cancelTokenSource);
      const allMandates = await getAllMandatesWithDeleted(cancelTokenSource);
      const superMandateSearchOptions = mapToDropdownOptionArray(allMandates, "name");

      this.setState({
        guarantorCompanies: guarantorCompanies !== null ? guarantorCompanies : [],
        allGuarantorCompanies,
        superMandateSearchOptions,
        searchValues: {...initialCompaniesSearch, guarantorCompanies: guarantorCompanies
              ? mapToDropdownOptionArray(guarantorCompanies, "description", "id")
              : []}
      });
    } finally {
      this.setState({
        referenceDataLoaded: true
      });
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findCompanyById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, company?: UpsertCompanyDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        company: company,
        companyId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      companyId: undefined,
      company: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        companiesLoaded: true
      })
    };

    searchCompanies(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCompanies: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteCompany = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteCompany(id, cancelTokenSource).then(response => {
      this.props.setSelectedCompanyId(undefined);
      this.setState(({foundCompanies}: State) => ({
        foundCompanies: foundCompanies.filter((company: CompanyDto) => company.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: CompanyFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const onFinally = () => {
      this.setState({
        companiesLoaded: true
      })
    };

    const {cancelTokenSource} = this.state;
    this.setState({companiesLoaded: false});
    searchCompanies(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundCompanies: response,
              searchValues: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {companiesLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchCompanySegment()}
          </div>
          <StyledViewTableRow>
            {!companiesLoaded
                ? <LoaderComponent message={getMessage("company.loading")}/>
                : this.renderCompaniesSegment()}
          </StyledViewTableRow>
          {this.renderUpsertCompanyModal()}
        </PraxStyledSegment>
    );
  }

  resetIncludesInactive = createDecorator({
    field: 'selfOnboarding',
    updates: {
      includesInactive: (fieldValue, allValues?: Partial<CompanySearch>) => {
        if (allValues && allValues.inactiveOnly && fieldValue) {
          return false;
        }
        return !(!fieldValue || !allValues);
      }
    }
  });

  resetIncludesInactiveWhenInactiveOnly = createDecorator({
    field: 'inactiveOnly',
    updates: {
      includesInactive: (fieldValue, allValues?: Partial<CompanySearch>) => {
        if (fieldValue) {
          return false;
        }
      }
    }
  });

  disableGuarantorToggle = createDecorator({
    field: 'guarantorCompanyId',
    updates: {
      guarantorCompany: (fieldValue) => {
        if (fieldValue) {
          return false;
        }
        return fieldValue;
      }
    }
  });

  resetGuarantorCompany = createDecorator({
    field: 'mandateId',
    updates: (value, name, allValues?: Partial<CompanySearch>) => {
      if (allValues) {
        const { allGuarantorCompanies } = this.state;

        let modifiedGuarantorCompanies = allGuarantorCompanies.filter(com => com.mandateId === value);
        return {
          guarantorCompanies: mapToDropdownOptionArray(modifiedGuarantorCompanies, "description", "id"),
          guarantorCompanyId: undefined
        }
      }
      return {};
    }
  })

  renderSearchCompanySegment = (): JSX.Element => {
    const {searchValues} = this.state;
    return (
        <FinalForm
            onSubmit={() => {
            }}
            decorators={[
              this.resetIncludesInactive,
              this.disableGuarantorToggle,
              this.resetIncludesInactiveWhenInactiveOnly,
              this.resetGuarantorCompany
            ]}
            initialValues={{...searchValues}}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage, currentUser} = this.props;
    const {
      referenceDataLoaded,
      foundCompanies,
      repetitiveTestingModeOptions,
      superMandateSearchOptions
    } = this.state;

    const numResults = foundCompanies.length;

    const disableGuarantorCheckBox = values.guarantorCompanyId !== undefined;
    const disabledIncludeInactive = values.inactiveOnly;
    const isSuperMandate = currentUser!.userConfig.modifyPatientsInOtherMandates;

    if (referenceDataLoaded) {
      return (
          <PxGrid columns={2}>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("description")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="description"
                    component={Input}
                    autoFocus
                    fluid
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("guarantor")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="guarantorCompanyId"
                    component={Select}
                    options={values.guarantorCompanies}
                    clearable
                    fluid
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("city")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="city"
                    component={Input}
                    fluid
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("company.repetitiveTestingMode")}:
              </Grid.Column>
              <Grid.Column width={5} verticalAlign={"top"}>
                <Field
                    name="repetitiveTestingMode"
                    component={Select}
                    options={repetitiveTestingModeOptions}
                    clearable
                    fluid
                />
              </Grid.Column>
            </Grid.Row>
            {isSuperMandate &&
                <Grid.Row>
                  <Grid.Column width={2} verticalAlign="middle">
                    {getMessage("patientCentral.overview.search.superMandate")}
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <Field
                        name="mandateId"
                        component={Select}
                        fluid
                        clearable
                        options={superMandateSearchOptions}
                    />
                  </Grid.Column>
                </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={3} verticalAlign={"top"}>
                <Field
                    name="includesInactive"
                    component={CheckBox}
                    label={getMessage("includingInactive")}
                    disabled={disabledIncludeInactive}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={3} verticalAlign={"top"}>
                <Field
                    name="inactiveOnly"
                    component={CheckBox}
                    label={getMessage("inactiveOnly")}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={3} verticalAlign={"top"}>
                <Field
                    name="selfOnboarding"
                    component={CheckBox}
                    label={getMessage("company.selfOnboarded")}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={3} verticalAlign={"top"}>
                <Field
                    name="guarantorCompany"
                    component={CheckBox}
                    label={getMessage("company.guarantorCompany")}
                    disabled={disableGuarantorCheckBox}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={4} textAlign="right">
                <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
              </Grid.Column>
            </Grid.Row>
            <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
            <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
          </PxGrid>
      )

    }
  };

  registrationStateCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    const tooltipTitle = getMessage(`registrationState.${cellData}`);

    if (cellData === "REJECTED") {
      return <Icon title={tooltipTitle} name="minus circle" color="red" size="large"/>
    } else if (cellData === "ACCEPTED") {
      return <Icon title={tooltipTitle} name="check circle" color="green" size="large"/>
    } else if (cellData === "PENDING") {
      return <Icon title={tooltipTitle} name="question circle" color="blue" size="large"/>
    } else if (cellData === "INITIAL") {
      return <Icon title={tooltipTitle} name="circle outline" color="grey" size="large"/>
    }
    return "";
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("company.active")
    }
    return getMessage("company.inactive");
  };

  selfOnBoardCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("SO");
    }
    return getMessage("");
  };

  guarantorCompanyCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("GC");
    }
    return getMessage("");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("company.confirmDelete")}
              onConfirm={() => this.deleteCompany(cellData)}
          />
        </React.Fragment>
    );
  };

  companiesRowGetter = ({index}: any) => {
    const {foundCompanies} = this.state;

    Object.assign(foundCompanies[index], {index: index + 1});
    return foundCompanies[index];
  };

  companiesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundCompanies} = this.state;

    const companyId = foundCompanies[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(companyId)}
        >
          {columns}
        </div>
    );
  };

  renderCompaniesSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundCompanies} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundCompanies.length}
            rowGetter={this.companiesRowGetter}
            rowRenderer={this.companiesRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 150,
                label: (getMessage("employee.registrationState")),
                dataKey: "registrationState",
                cellRenderer: this.registrationStateCellRenderer
              },
              {
                width: 150,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 80,
                label: (getMessage("SO")),
                dataKey: "selfOnboarding",
                cellRenderer: this.selfOnBoardCellRenderer
              },
              {
                width: 80,
                label: (getMessage("GC")),
                dataKey: "guarantorCompany",
                cellRenderer: this.guarantorCompanyCellRenderer
              },
              {
                width: 700,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 500,
                label: (getMessage("city")),
                dataKey: "city"
              },
              {
                width: 500,
                label: (getMessage("street")),
                dataKey: "street"
              },
              {
                width: 500,
                label: (getMessage("numberOfEmployees")),
                dataKey: "numberOfEmployees"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };

  renderUpsertCompanyModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, company, companyId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("company.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertCompanyForm
                company={company}
                companyId={companyId}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(withAuthContext(withGuarantorsContext(CompaniesView)));
